{ /* Generated by Copilot- Start */ }
import React, { createContext, useRef, ReactNode } from "react";
import { fireErrorDisplayedGA } from "@directv-digital/dds.services.analytics-service";

// Flat value shape returned by useComparePkgTrayContext — unchanged public API so
// the single-instance tray consumers (ComparePackagesTray, ComparePackagesTrayModule)
// keep destructuring the same fields.
export interface ComparePkgTrayContextValue {
    selectedItems: any;
    trayOpen: boolean;
    updateSelectedTag: (tag: any) => void;
    setTrayOpen: (open: boolean) => void;
    collapsedTray: boolean;
    updateCollapsedTray: (collapsed: boolean) => void;
    maxSelectedTags?: number;
    isComparePkgTray?: boolean;
    compareText?: any;
    isMaxSelected?: boolean;
    triggerElementRef: React.MutableRefObject<HTMLElement | null>;
}

// The mutable slice that actually changes on interaction.
// `selectedItems` is `readonly` on purpose: the whole re-render mechanism rests on
// the invariant that it is only ever REPLACED (spread/filter), never mutated in
// place. `readonly` makes a `getState().selectedItems.push(id)` a compile error —
// such a mutation would change internal state with an unchanged reference, so no
// subscriber would re-render and the UI would silently go stale. (Scope: this guards
// getState() callers, who get this CompareState type; the public snapshot field
// `ComparePkgTrayContextValue.selectedItems` stays `any` for API parity.)
interface CompareState {
  selectedItems: readonly string[];
  trayOpen: boolean;
  collapsedTray: boolean;
  isMaxSelected: boolean;
}

// External store backing the context. Consumers subscribe (via the useStoreSlice
// hook in useComparePkgTrayContext) and read EITHER the full flat snapshot (tray UI,
// needs most fields) OR a narrow slice (a per-card CompareBox reads only its own
// `selectedItems.includes(id)` boolean). Narrow selectors are what stop the per-card
// re-render cascade: toggling one card no longer re-renders every other card's
// checkbox. Actions are imperative (read latest via getState) so event handlers
// never need to subscribe.
export interface ComparePkgTrayStore {
  subscribe: (listener: () => void) => () => void;
  getState: () => CompareState;
  getSnapshot: () => ComparePkgTrayContextValue;
  updateSelectedTag: (tag: any) => void;
  setTrayOpen: (open: boolean) => void;
  updateCollapsedTray: (collapsed: boolean) => void;
  triggerElementRef: React.MutableRefObject<HTMLElement | null>;
}

export const ComparePkgTrayContext = createContext<ComparePkgTrayStore | undefined>(
  undefined
);

export interface ComparePkgTrayProviderProps {
  children: ReactNode;
  maxSelectedTag?: number;
  isComparePkgTray?: boolean;
  compareText?: any;
}

export function ComparePkgTrayProvider({
  children,
  maxSelectedTag,
  isComparePkgTray,
  compareText
}: ComparePkgTrayProviderProps) {
  const triggerElementRef = useRef<HTMLElement | null>(null);

  // Latest CMS-driven config kept in a ref so the store (created once) reads fresh
  // values without being recreated when props change. getSnapshot() reads this ref
  // on every call, so a changed compareText / maxSelectedTag / isComparePkgTray is
  // always reflected (no stale cache).
  const configRef = useRef({
    maxSelectedTags: Number(maxSelectedTag) || 3,
    isComparePkgTray,
    compareText
  });
  configRef.current = {
    maxSelectedTags: Number(maxSelectedTag) || 3,
    isComparePkgTray,
    compareText
  };

  const storeRef = useRef<ComparePkgTrayStore>();
  if (!storeRef.current) {
    let state: CompareState = {
      selectedItems: [],
      trayOpen: false,
      collapsedTray: false,
      isMaxSelected: false
    };
    const listeners = new Set<() => void>();

    const emit = () => {
      listeners.forEach(listener => listener());
    };
    const setState = (patch: Partial<CompareState>) => {
      // Skip the listener sweep when the patch changes nothing (e.g. setTrayOpen(true)
      // when already open). The slice guards in useStoreSlice would bail out the
      // re-renders anyway, but short-circuiting here avoids the wasted notify pass.
      const changed = (Object.keys(patch) as (keyof CompareState)[]).some(
        key => !Object.is(state[key], patch[key])
      );
      if (!changed) {
        return;
      }
      state = {
        ...state,
        ...patch
      };
      emit();
    };

    const updateSelectedTag = (tag: any) => {
      const { maxSelectedTags } = configRef.current;
      if (tag !== "") {
        if (state.selectedItems.length < maxSelectedTags || state.selectedItems.includes(tag)) {
          const isAlreadySelected = state.selectedItems.includes(tag);
          const selectedItems = isAlreadySelected ?
            state.selectedItems.filter(item => item !== tag) :
            [...state.selectedItems, tag];
          setState({
            selectedItems,
            isMaxSelected: false
          });
        } else {
          setState({ isMaxSelected: true });
          fireErrorDisplayedGA('MAX_COMPARE_LIMIT', `Maximum of ${maxSelectedTags} packages can be compared`);
        }
      } else {
        setState({
          selectedItems: [],
          isMaxSelected: false
        });
      }
    };
    const setTrayOpen = (open: boolean) => setState({ trayOpen: open });
    const updateCollapsedTray = (collapsed: boolean) => setState({ collapsedTray: collapsed });

    const buildSnapshot = (): ComparePkgTrayContextValue => {
      const { maxSelectedTags, isComparePkgTray: isCmp, compareText: text } = configRef.current;
      return {
        selectedItems: state.selectedItems,
        trayOpen: state.trayOpen,
        collapsedTray: state.collapsedTray,
        isMaxSelected: state.isMaxSelected,
        maxSelectedTags,
        isComparePkgTray: isCmp,
        compareText: text,
        updateSelectedTag,
        setTrayOpen,
        updateCollapsedTray,
        triggerElementRef
      };
    };

    storeRef.current = {
      subscribe: (listener: () => void) => {
        listeners.add(listener);
        return () => {
          listeners.delete(listener);
        };
      },
      getState: () => state,
      // Built fresh each call so config (compareText / maxSelectedTag /
      // isComparePkgTray) and state are always current — no stale cache. Full-snapshot
      // consumers compare the whole object with shallowEqual (field-level Object.is),
      // not Object.is on the object itself — a fresh object every call would otherwise
      // always report "changed" and re-render every consumer, re-introducing the
      // cascade this store removes. So allocating a new object here is not a perf
      // concern (few full consumers, all shallowEqual-guarded).
      getSnapshot: () => buildSnapshot(),
      updateSelectedTag,
      setTrayOpen,
      updateCollapsedTray,
      triggerElementRef
    };
  }

  return (
    <ComparePkgTrayContext.Provider value={storeRef.current}>
      {children}
    </ComparePkgTrayContext.Provider>
  );
}
{ /* Generated by Copilot- End */ }
